import { useState, useRef, useCallback, useEffect } from "react";
import { Printer } from "lucide-react";
import { toast } from "react-toastify";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { logoutProcessor } from "../helpers/logoutProcessor";
import { useTranslation } from "../../i18n";
import { useNavigate } from "react-router-dom";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import type { DeskInfo, DispatchOrder, DispatchBox, FlaggedPackState } from "../../types/Dispatch";
import type { ReopenResult } from "./pack-state";
import { useDemoVisible } from "../helpers/demoMode";
import { apiFetch } from "../../api/apiFetch";
import DispatchPrinterButton from "./dispatch-printer-button";
import DispatchReopenButton from "./dispatch-reopen-button";
import DispatchLabelReopenModal from "./dispatch-label-reopen-modal";
import DispatchSessionBar from "./dispatch-session-bar";
import { DispatchSessionStats } from "./dispatch-session-chrome";
import "../../styles/dispatch-bench.css";
import { claimNotice } from "./deskOrderState";

// What the BE tells us about reopening a unit resolved from a scanned
// SHIPPING LABEL. `required` is the whole decision: false means the order
// is already back at 'dispatch' (it was reopened from the Orders modal,
// which voided the labels) so it opens straight up; true means it is still
// PACKED and the operator has to confirm, because reopening voids the
// live consignment(s) and retracts the channel shipment push. `status`
// is whichever packed status it holds — 'fulfilled', or 'flagged' /
// 'partially_refunded' / 'refunded' once a flag has been raised on it.
export type UnitReopenInfo = {
    required: boolean;
    status: string;
    labelCount: number;
    waybills: string[];
    pastCutoff: boolean;
    // Why the void is refused: APC past its manifest cutoff, or InPost has
    // already scanned a parcel (their voids are local, so a scanned parcel
    // is travelling and billed). Absent/null while still cancellable.
    pastCutoffReason?: "manifested" | "already_scanned" | null;
    cutoff: string | null;
};

export type ResolvedUnit = {
    // zone_tote = a Sorter 2 (Zone Storage) tote bound at the close-tag
    // scan; one order per tote, packed like a P&S dispatch tote.
    unitType: "large_tote" | "light_tote" | "pas_location" | "zone_tote" | "shipping_label";
    warehouse: string;
    tote_id?: string;
    label?: string;
    // Every tote a sorted (Putwall) order went out in, with what each
    // holds. Two entries for a two-cubby order: the packer fetches the
    // second tote before packing. Absent / one entry otherwise.
    totes?: { tote_id: string; items: number }[];
    orders: DispatchOrder[];
    // Only on a shipping-label reopen: the pack desk as it was when the
    // order shipped, rebuilt by the BE from the parcels the reopen just
    // deleted. The boxes are still taped up in front of the operator, so
    // the desk restores them instead of asking for every unit again.
    packState?: FlaggedPackState | null;
};

type Props = {
    deskInfo: DeskInfo;
    // May return a promise: the desk opens the order on the server first, and
    // the scan stays locked until it has answered.
    onUnitResolved: (unit: ResolvedUnit) => void | Promise<unknown>;
    goBack: () => void;
    onChangeDesk?: () => void;
    autoPrint?: boolean;
    onToggleAutoPrint?: () => void;
    // Last shipped order — reopenable from here too, so finishing a tote's
    // final order (which lands back on this screen) doesn't strand the
    // operator with no way to pull it back.
    lastOrder?: { order: DispatchOrder; boxes: DispatchBox[] } | null;
    onReopenOrder?: (data: { order: DispatchOrder; boxes: DispatchBox[] }) => void;
    // Reverse a shipped order's fulfilment server-side (voids the
    // consignment, deletes the parcels, retracts unsent channel pushes) and
    // report what the carrier actually said. Resolves the reopen result —
    // carrying the pre-delete pack snapshot — when the order is back at
    // 'dispatch' and safe to pack again, or null when it isn't.
    onReopenShippedOrder?: (orderId: string) => Promise<ReopenResult | null>;
};

const DispatchScanTote = ({ deskInfo, onUnitResolved, goBack, onChangeDesk, autoPrint, onToggleAutoPrint, lastOrder, onReopenOrder, onReopenShippedOrder }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const DEMO_MODE = useDemoVisible();
    const [scan, setScan] = useState("");
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [loading, setLoading] = useState(false);
    const [demoUnits, setDemoUnits] = useState<string[]>([]);
    // A scanned shipping label whose order has already shipped — held here
    // while the operator confirms the reopen. The barcode is kept so the
    // unit can be re-resolved from the BE afterwards rather than patching a
    // now-stale order in place.
    const [pendingLabel, setPendingLabel] = useState<{ barcode: string; order: DispatchOrder; reopen: UnitReopenInfo } | null>(null);
    const [reopening, setReopening] = useState(false);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Pull ALL dispatch-ready units (closed Single/Large totes with picked
    // orders + sorted P&S locations) for the demo button row, so the operator
    // can tap any tote that's ready to be packed without a hardware scanner.
    useEffect(() => {
        if (!DEMO_MODE || !deskInfo.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/dispatch/ready-units?warehouse=${encodeURIComponent(deskInfo.warehouse)}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive) setDemoUnits(Array.isArray(data.units) ? data.units : []);
            } catch { /* hide panel */ }
        })();
        return () => { alive = false; };
    }, [DEMO_MODE, deskInfo.warehouse]);

    const flash = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 400);
    };

    // Resolve a barcode to a unit. Returns the parsed body so the reopen
    // confirmation can re-resolve the same label once the order is back at
    // 'dispatch' — the BE is then the only thing deciding what is packable.
    const resolve = useCallback(async (barcode: string) => {
        const res = await apiFetch(`/dispatch/resolve-unit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ barcode }),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return null; }
        return { ok: res.ok, data: await res.json() };
    }, [navigate]);

    const handleSubmit = useCallback(async (scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        if (!val || loading || pendingLabel) return;
        setScan("");
        setLoading(true);
        try {
            const out = await resolve(val);
            if (!out) return;
            const { ok, data } = out;
            // A tote whose ONE order another packer holds at a desk (desk
            // session): say who and where, and don't open it. A multi-order
            // tote goes on — the other orders still open normally.
            const blockedBy = ok && data.ok && Array.isArray(data.orders) && data.orders.length === 1
                && data.unitType !== "light_tote" && !data.reopen?.required
                ? data.orders[0]?.claimed_by ?? null
                : null;
            if (blockedBy) {
                playError();
                flash("error");
                toast.dismiss();
                toast.error(claimNotice(t, blockedBy), { toastId: "desk-order-claimed", autoClose: 15000 });
                return;
            }
            if (ok && data.ok && Array.isArray(data.orders) && data.orders.length > 0) {
                playSuccess();
                flash("success");
                // A scanned shipping label whose order is STILL shipped:
                // hold it behind the confirmation instead of opening it.
                // Everything else (and a label whose order was already
                // reopened) goes straight through.
                if (data.reopen?.required) {
                    setPendingLabel({ barcode: val, order: data.orders[0], reopen: data.reopen });
                    return;
                }
                await onUnitResolved({
                    unitType: data.unitType, warehouse: data.warehouse,
                    tote_id: data.tote_id, label: data.label, orders: data.orders,
                    totes: Array.isArray(data.totes) ? data.totes : undefined,
                    // Present when the BE could rebuild the desk of an order
                    // that was packed and then reopened elsewhere (from the
                    // Orders modal, or before this desk's build could
                    // snapshot it) — see attachRecoveredPackState.
                    packState: data.pack_state ?? null,
                });
            } else {
                playError();
                flash("error");
                toast.dismiss();
                const reason = data?.reason;
                if (reason === "pick_and_sort_needs_sorting") toast.error(t("dispatch_tote_needs_sorting"));
                else if (reason === "not_sorted_yet") toast.error(t("dispatch_tote_needs_sorting"));
                else if (reason === "no_ready_orders") toast.error(t("dispatch_tote_no_orders"));
                // Two-cubby Putwall order with only one tote bound so far —
                // the rest is still in a cubby on the wall. Name it.
                else if (reason === "pas_partly_on_wall") {
                    toast.error(data.label
                        ? t("dispatch_tote_partly_on_wall", { label: String(data.label) })
                        : t("dispatch_tote_partly_on_wall_unknown"));
                }
                // A shipping label that resolved to an order this desk can't
                // pack (cancelled, refunded, on hold, flagged). Say which,
                // rather than the misleading "tote not recognised".
                else if (reason === "order_not_dispatchable") {
                    toast.error(t("dispatch_label_order_not_dispatchable", {
                        order: data.order_number ?? "",
                        status: t(`status_${data.status}`),
                    }));
                }
                else toast.error(t("dispatch_tote_unknown"));
            }
        } catch {
            playError();
            flash("error");
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            setLoading(false);
        }
    }, [scan, loading, pendingLabel, resolve, onUnitResolved, t]);

    // Confirmed: reverse the fulfilment server-side, then re-resolve the
    // SAME label so the order arrives with its post-reopen state ('dispatch',
    // no parcels) rather than the stale copy the scan returned.
    //
    // The reopen's pack snapshot rides along with the re-resolved order:
    // the parcels it describes are deleted by the time we re-resolve, but
    // the physical boxes are still on the bench, so the desk restores them
    // instead of making the operator rescan a packed order.
    const confirmLabelReopen = useCallback(async () => {
        if (!pendingLabel || reopening || !onReopenShippedOrder) return;
        // Reversed at the carrier but the order no longer resolves here —
        // clear the desk rather than leaving a modal over a dead order.
        const giveUp = () => {
            playError();
            toast.dismiss();
            toast.error(t("reopen_failed"), { toastId: "reopen-err" });
            setPendingLabel(null);
        };
        setReopening(true);
        try {
            // A failed reversal has already toasted what the carrier said —
            // leave the modal up so the operator can retry or back out.
            const reopened = await onReopenShippedOrder(pendingLabel.order.order_id);
            if (!reopened) return;
            const out = await resolve(pendingLabel.barcode);
            if (!out) return;
            const { ok, data } = out;
            if (!ok || !data.ok || !Array.isArray(data.orders) || data.orders.length === 0) {
                giveUp();
                return;
            }
            setPendingLabel(null);
            await onUnitResolved({
                unitType: data.unitType, warehouse: data.warehouse,
                tote_id: data.tote_id, label: data.label, orders: data.orders,
                // The reopen's own snapshot is exact (built from the parcels
                // it deleted); the re-resolve's is a log replay. Prefer the
                // former, fall back so an older BE still restores something.
                packState: reopened.packState ?? data.pack_state ?? null,
            });
        } catch {
            giveUp();
        } finally {
            setReopening(false);
        }
    }, [pendingLabel, reopening, onReopenShippedOrder, resolve, onUnitResolved, t]);

    return (
        <div
            className="scan-panel dispatch-scan dispatch-bench dispatch-scan--typeable"
            style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            {/* Session bar: the desk this packer is on, as a labelled chip
                (tap to change desk — it used to be an unlabelled "D-04"). */}
            <DispatchSessionBar
                deskId={deskInfo.deskId}
                onChangeDesk={onChangeDesk}
                showPoints
                extra={onReopenOrder && <DispatchReopenButton lastOrder={lastOrder} onReopen={onReopenOrder} />}
                onBack={goBack}
            />

            <div className="scan-centre">
                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                    {t("scan_tote").toUpperCase().split(" ").map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                </div>
                {/* A shipping label works here too — the only way to reach an
                    order whose tote has already been recycled. Say so, or
                    nobody discovers it. */}
                {onReopenShippedOrder && (
                    <div className={`scan-command-sub ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("dispatch_scan_label_hint")}
                    </div>
                )}
            </div>

            <HiddenScanInput value={scan} onChange={setScan} onSubmit={handleSubmit} disabled={loading || !!pendingLabel} disableCamera placeholder={t("scan_tote")} />

            {/* Bottom bar: printer picker + auto-print toggle — reachable
                before any order opens, so both can be changed even with
                auto-print enabled — and the session counts. */}
            <div className="dispatch-scan-footer">
                <DispatchPrinterButton />
                {onToggleAutoPrint && (
                    <div className="dispatch-auto-print-toggle" onClick={onToggleAutoPrint}>
                        <Printer size={18} />
                        <span>{t("auto_print")}</span>
                        <div className={`dispatch-toggle ${autoPrint ? "dispatch-toggle--on" : ""}`}>
                            <div className="dispatch-toggle-knob" />
                        </div>
                    </div>
                )}
                <div className="dispatch-scan-footer-spacer" />
                <DispatchSessionStats />
            </div>

            {pendingLabel && (
                <DispatchLabelReopenModal
                    order={pendingLabel.order}
                    reopen={pendingLabel.reopen}
                    busy={reopening}
                    onConfirm={confirmLabelReopen}
                    onCancel={() => setPendingLabel(null)}
                />
            )}

            {DEMO_MODE && demoUnits.length > 0 && (
                <div className="dispatch-demo-panel">
                    <span className="dispatch-demo-label">Demo</span>
                    {demoUnits.map(u => (
                        <button key={u} className="dispatch-demo-btn" onClick={() => handleSubmit(u)} disabled={loading}>{u}</button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DispatchScanTote;
